import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Send, Plus, Users as UsersIcon, MessageCircle, Hash, Search, Check } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Chat() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState("team");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [users, setUsers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newType, setNewType] = useState("dm"); // "dm" | "group"
  const [newSelected, setNewSelected] = useState(new Set());
  const [newName, setNewName] = useState("");
  const [newFilter, setNewFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const boxRef = useRef(null);

  async function loadThreads(preserveActive = true) {
    const { data } = await api.get("/chat/threads");
    setThreads(data);
    if (!preserveActive || !activeId) {
      setActiveId(data[0]?.id || "team");
    }
    setLoading(false);
  }

  async function loadMessages(id) {
    if (!id) return;
    try {
      const { data } = await api.get(`/chat/threads/${id}/messages`);
      setMessages(data);
    } catch (err) {
      console.warn("Chat: unable to load messages", err);
    }
  }

  useEffect(() => {
    loadThreads(false);
    api.get("/users").then(r => setUsers(r.data || []))
      .catch(err => console.warn("Chat: users load failed", err));
  }, []);

  useEffect(() => {
    loadMessages(activeId);
    const i = setInterval(() => {
      loadMessages(activeId);
      loadThreads(true);
    }, 4000);
    return () => clearInterval(i);
  }, [activeId]);

  useEffect(() => {
    boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const activeThread = useMemo(
    () => threads.find(t => t.id === activeId) || null,
    [threads, activeId]
  );

  async function send(e) {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const value = text;
    setText("");
    try {
      await api.post(`/chat/threads/${activeId}/messages`, { text: value });
      loadMessages(activeId);
      loadThreads(true);
    } catch {
      toast.error("Nachricht konnte nicht gesendet werden");
      setText(value);
    }
  }

  async function createThread() {
    const parts = Array.from(newSelected);
    if (newType === "dm" && parts.length !== 1) {
      toast.error("Bitte genau eine Person auswählen");
      return;
    }
    if (newType === "group" && parts.length < 2) {
      toast.error("Bitte mindestens 2 Personen auswählen");
      return;
    }
    if (newType === "group" && !newName.trim()) {
      toast.error("Gruppenname erforderlich");
      return;
    }
    try {
      const { data } = await api.post("/chat/threads", {
        type: newType,
        participants: parts,
        name: newType === "group" ? newName.trim() : "",
      });
      setNewOpen(false);
      setNewSelected(new Set());
      setNewName("");
      setNewFilter("");
      await loadThreads(false);
      setActiveId(data.id);
      toast.success(newType === "dm" ? "Direktnachricht bereit" : "Gruppe erstellt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Erstellen");
    }
  }

  const filteredUsers = useMemo(() => {
    const q = newFilter.trim().toLowerCase();
    const others = users.filter(u => u.id !== user?.id && u.role !== "customer");
    if (!q) return others;
    return others.filter(u => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, newFilter, user]);

  function toggleSelect(uid) {
    setNewSelected(prev => {
      const n = new Set(prev);
      if (newType === "dm") {
        // Only one at a time
        n.clear();
        if (!prev.has(uid)) n.add(uid);
      } else {
        if (n.has(uid)) n.delete(uid); else n.add(uid);
      }
      return n;
    });
  }

  const iconFor = (t) => {
    if (t.type === "team") return <Hash className="w-4 h-4" />;
    if (t.type === "group") return <UsersIcon className="w-4 h-4" />;
    return <MessageCircle className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="chat-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Kommunikation</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Chat</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Team-Channel, Direktnachrichten und Gruppen
          </p>
        </div>
        <Button className="rounded-full" onClick={() => { setNewOpen(true); setNewType("dm"); }} data-testid="btn-new-chat">
          <Plus className="w-4 h-4 mr-1.5" /> Neuer Chat
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-260px)] min-h-[520px]">
        {/* Sidebar */}
        <Card className="col-span-4 lg:col-span-3 rounded-3xl border-border/60 glass p-3 flex flex-col overflow-hidden" data-testid="chat-threads">
          <div className="px-2 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {threads.length} Unterhaltungen
          </div>
          <div className="flex-1 overflow-auto space-y-1 pr-1">
            {loading && <div className="text-center py-8 text-sm text-muted-foreground">Lädt...</div>}
            {threads.map(t => {
              const active = t.id === activeId;
              return (
                <button key={t.id}
                        onClick={() => setActiveId(t.id)}
                        data-testid={`thread-${t.id}`}
                        className={`w-full text-left rounded-2xl p-3 transition flex items-start gap-3 ${
                          active ? "bg-primary/15 border border-primary/40" : "hover:bg-secondary/60"
                        }`}>
                  <div className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 ${
                    active ? "bg-primary/25 text-primary" : "bg-secondary text-muted-foreground"
                  }`}>{iconFor(t)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-sm truncate">{t.display_name || "—"}</div>
                      {t.unread > 0 && (
                        <Badge className="rounded-full bg-accent text-accent-foreground text-[10px] px-1.5 py-0">{t.unread}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t.last_message
                        ? `${t.last_message.sender_name?.split(" ")[0] || "?"}: ${t.last_message.text}`
                        : (t.type === "team" ? "Kanal für alle" : "Noch keine Nachrichten")}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Messages */}
        <Card className="col-span-8 lg:col-span-9 rounded-3xl border-border/60 glass flex flex-col overflow-hidden" data-testid="chat-conversation">
          {activeThread && (
            <div className="px-6 py-3 border-b border-border/60 flex items-center gap-3">
              <div className="w-9 h-9 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
                {iconFor(activeThread)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold truncate">{activeThread.display_name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {activeThread.type === "team" && "Sichtbar für alle Teammitglieder"}
                  {activeThread.type === "group" && `Gruppe · ${activeThread.participant_names?.join(", ")}`}
                  {activeThread.type === "dm" && "Direktnachricht"}
                </div>
              </div>
            </div>
          )}

          <div ref={boxRef} className="flex-1 overflow-auto p-6 space-y-3" data-testid="chat-messages">
            {messages.map(m => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                    mine ? "bg-primary text-primary-foreground" : "bg-secondary"
                  }`}>
                    {!mine && (
                      <div className="text-[11px] font-semibold text-muted-foreground mb-0.5">
                        {m.sender_name}
                        {m.sender_role && m.sender_role !== "customer" && (
                          <span className="ml-1 opacity-70">· {m.sender_role}</span>
                        )}
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[10px] mt-0.5 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      {new Date(m.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
            {!loading && messages.length === 0 && (
              <div className="text-center text-muted-foreground py-12">
                Noch keine Nachrichten. Schreibe die erste!
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-4 border-t border-border/60 flex items-center gap-2" data-testid="chat-form">
            <Input value={text} onChange={e => setText(e.target.value)}
                   placeholder="Nachricht schreiben..."
                   className="rounded-full h-11 px-5" data-testid="chat-input" />
            <Button type="submit" size="icon" className="rounded-full h-11 w-11 shrink-0" data-testid="chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </Card>
      </div>

      {/* New chat dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle>Neuer Chat</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-2">
            <Button variant={newType === "dm" ? "default" : "outline"} className="rounded-full flex-1"
                    onClick={() => { setNewType("dm"); setNewSelected(new Set()); }}
                    data-testid="new-type-dm">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Direkt
            </Button>
            <Button variant={newType === "group" ? "default" : "outline"} className="rounded-full flex-1"
                    onClick={() => { setNewType("group"); setNewSelected(new Set()); }}
                    data-testid="new-type-group">
              <UsersIcon className="w-4 h-4 mr-1.5" /> Gruppe
            </Button>
          </div>
          {newType === "group" && (
            <Input value={newName} onChange={e => setNewName(e.target.value)}
                   placeholder="Gruppenname (z.B. Team Berlin)"
                   className="rounded-full h-10 px-4 mb-2" data-testid="new-group-name" />
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={newFilter} onChange={e => setNewFilter(e.target.value)}
                   placeholder="Person suchen..."
                   className="rounded-full h-10 pl-9" />
          </div>
          <div className="max-h-60 overflow-auto space-y-1 mt-1">
            {filteredUsers.map(u => {
              const sel = newSelected.has(u.id);
              return (
                <button key={u.id} type="button" onClick={() => toggleSelect(u.id)}
                        data-testid={`new-user-${u.id}`}
                        className={`w-full rounded-2xl p-2.5 flex items-center gap-3 transition ${
                          sel ? "bg-primary/15 border border-primary/40" : "hover:bg-secondary/60"
                        }`}>
                  <div className={`w-6 h-6 rounded-md shrink-0 flex items-center justify-center ${
                    sel ? "bg-primary text-primary-foreground" : "bg-background border border-border"
                  }`}>{sel && <Check className="w-3.5 h-3.5" />}</div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-semibold truncate">{u.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate capitalize">{u.role} · {u.email}</div>
                  </div>
                </button>
              );
            })}
            {filteredUsers.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-6">Keine Personen gefunden</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setNewOpen(false)}>Abbrechen</Button>
            <Button className="rounded-full" onClick={createThread} data-testid="new-chat-create">
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
